import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { KeyRound, ShieldAlert, MonitorSmartphone, CheckCircle2, AlertCircle } from 'lucide-react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { useSecuritySettings } from '../../../hooks/useSecuritySettings';
import { logoutUser } from '../../../store/authSlice';

const SecurityTab = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { updatePassword, logoutOtherSessions, loading } = useSecuritySettings();

  // Password Form State
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [pwdError, setPwdError] = useState('');
  const [pwdSuccess, setPwdSuccess] = useState('');

  // Session State
  const [sessionError, setSessionError] = useState('');
  const [sessionSuccess, setSessionSuccess] = useState('');
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPwdError('');
    setPwdSuccess('');

    if (passwords.new.length < 6) {
      setPwdError('Password must be at least 6 characters long.');
      return;
    }
    if (passwords.new === passwords.current) {
      setPwdError('New password cannot be the same as your current password.');
      return;
    }
    if (passwords.new !== passwords.confirm) {
      setPwdError('Passwords do not match.');
      return;
    }

    const { success, error } = await updatePassword(user?.email, passwords.current, passwords.new);
    if (success) {
      setPwdSuccess('Your password has been updated successfully!');
      setPasswords({ current: '', new: '', confirm: '' });
    } else {
      setPwdError(error || 'Failed to update password.');
    }
  };

  const handleLogoutOtherSessions = async () => {
    setSessionError('');
    setSessionSuccess('');
    setIsLoggingOut(true);

    const { success, error } = await logoutOtherSessions();
    if (success) {
      setSessionSuccess('Successfully logged out of all other devices.');
      setIsLoggingOut(false);
    } else {
      setSessionError(error || 'Failed to logout of other devices.');
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Change Password Section */}
      <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
        <div className="px-8 py-6 border-b border-border bg-muted/20">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-primary/20 text-primary rounded-lg">
              <KeyRound size={20} />
            </div>
            <h2 className="text-lg font-bold text-foreground">Change Password</h2>
          </div>
          <p className="text-sm text-muted-foreground ml-11">Ensure your account is using a long, random password to stay secure.</p>
        </div>
        
        <div className="p-8">
          {pwdSuccess && (
            <div className="mb-6 flex items-center gap-2 p-4 text-green-700 bg-green-50 rounded-lg border border-green-200">
              <CheckCircle2 size={20} />
              <p className="text-sm font-medium">{pwdSuccess}</p>
            </div>
          )}
          
          {pwdError && (
            <div className="mb-6 flex items-center gap-2 p-4 text-red-700 bg-red-50 rounded-lg border border-red-200">
              <AlertCircle size={20} />
              <p className="text-sm font-medium">{pwdError}</p>
            </div>
          )}

          <form onSubmit={handlePasswordSubmit} className="max-w-md space-y-5">
            <Input
              label="Current Password"
              type="password"
              placeholder="Enter your current password"
              value={passwords.current}
              onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
              required
            />

            <Input
              label="New Password"
              type="password"
              placeholder="Enter your new password"
              value={passwords.new}
              onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
              required
            />
            
            <Input
              label="Confirm Password"
              type="password"
              placeholder="Confirm your new password"
              value={passwords.confirm}
              onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
              required
            />
            
            <Button 
              type="submit" 
              disabled={loading && !isLoggingOut} 
              loading={loading && !isLoggingOut}
              className="mt-2"
            >Update Password</Button>
          </form>
        </div>
      </div>
      {/* Session Management Section */}
      <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
        <div className="px-8 py-6 border-b border-border bg-muted/20">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-amber-100 text-amber-700 rounded-lg">
              <MonitorSmartphone size={20} />
            </div>
            <h2 className="text-lg font-bold text-foreground">Session Management</h2>
          </div>
          <p className="text-sm text-muted-foreground ml-11">Control where you are currently logged in to your account.</p>
        </div>
        
        <div className="p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="max-w-lg">
            <h3 className="text-md font-bold text-foreground flex items-center gap-2">Log out of all other devices</h3>
            <p className="text-sm text-muted-foreground mt-2">
              If you lost your device or notice suspicious activity, you can force a logout on all other devices. 
              Other sessions will be signed out immediately.
            </p>
            
            {sessionSuccess && (
              <div className="mt-4 flex items-center gap-2 p-3 text-green-700 bg-green-50 rounded-lg border border-green-200 text-sm font-medium">
                <CheckCircle2 size={16} />
                {sessionSuccess}
              </div>
            )}
            
            {sessionError && (
              <div className="mt-4 flex items-center gap-2 text-red-600 text-sm font-medium">
                <AlertCircle size={16} />
                {sessionError}
              </div>
            )}
          </div>
          
          <div className="flex-shrink-0 w-full sm:w-auto">
            <Button
              variant="outline"
              className="w-full sm:w-auto border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 group"
              onClick={handleLogoutOtherSessions}
              disabled={loading || isLoggingOut}
              loading={isLoggingOut}
            >
              <ShieldAlert size={16} className="mr-2 group-hover:animate-pulse" />Log out other sessions</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityTab;
